import React, { useEffect, useRef, useState } from "react";
import { Box, Skeleton, Typography, keyframes } from "@mui/material";
import {
  buildSessionStatusStyles,
  formatRelativeTime,
  formatSlackLastReplyTime,
  resolveSessionStatusChipKey,
  sessionRowReadiness,
  SESSION_ROW_AVATAR_RADIUS,
  SESSION_ROW_AVATAR_SIZE,
} from "./agentSessionsPanelUtils";
import { useStatusPalette } from "../../utils/statusColors";
import AgentAvatar from "./AgentAvatar";
import useProjectStore from "../ProjectCanvas/useProjectStore";

const REPLY_AVATAR_SIZE = 18;

function buildNeutralAvatarProps(name) {
  const useYou = name === "You";
  const useSystem = name === "System";
  if (!useYou && !useSystem) {
    return { name };
  }
  if (useSystem) {
    return {
      name: "System",
      children: "S",
      sx: {
        bgcolor: "text.primary",
        color: "background.paper",
      },
    };
  }
  return {
    name: null,
    children: "Y",
    sx: {
      bgcolor: "text.primary",
      color: "background.paper",
    },
  };
}

function ReplyAvatarStack({ participants, size = REPLY_AVATAR_SIZE }) {
  const unique = participants.filter(
    (name, index) => name && participants.indexOf(name) === index
  );
  const visible = unique.slice(0, 3);

  return (
    <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
      {visible.map((name, index) => (
        <Box
          key={`${name}-${index}`}
          sx={{
            ml: index > 0 ? "-5px" : 0,
            zIndex: visible.length - index,
            borderRadius: "4px",
            border: "2px solid",
            borderColor: "background.subtle",
            lineHeight: 0,
          }}
        >
          <AgentAvatar
            size={size}
            borderRadius="3px"
            fontSize="9px"
            {...buildNeutralAvatarProps(name)}
          />
        </Box>
      ))}
    </Box>
  );
}

const slideIn = keyframes`
  from {
    opacity: 0;
    max-height: 0;
    transform: translateX(-8px);
  }
  to {
    opacity: 1;
    max-height: 120px;
    transform: translateX(0);
  }
`;

const statusPulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.15); }
  100% { transform: scale(1); }
`;

function StatusBadge({ status, animate, compact = false, bare = false, config }) {
  const _sp = useStatusPalette();
  const STATUS_STYLES = config || buildSessionStatusStyles(_sp);
  const s =
    STATUS_STYLES[resolveSessionStatusChipKey(status)] ||
    STATUS_STYLES.IN_PROGRESS;
  if (bare) {
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 5,
            height: 5,
            borderRadius: "50%",
            backgroundColor: s.color,
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{
            fontSize: "10px",
            fontWeight: 500,
            color: s.color,
            lineHeight: 1,
            whiteSpace: "nowrap",
          }}
        >
          {s.label}
        </Typography>
      </Box>
    );
  }
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: compact ? "3px" : "4px",
        height: compact ? "16px" : "20px",
        px: compact ? "4px" : "6px",
        borderRadius: compact ? "4px" : "5px",
        backgroundColor: s.bg,
        border: `1px solid ${s.border}`,
        transition: "background-color 300ms ease, border-color 300ms ease",
        ...(animate && {
          animation: `${statusPulse} 350ms ease-out`,
        }),
      }}
    >
      <Box
        sx={{
          width: compact ? 4 : 5,
          height: compact ? 4 : 5,
          borderRadius: "50%",
          backgroundColor: s.color,
          flexShrink: 0,
          transition: "background-color 300ms ease",
        }}
      />
      <Typography
        sx={{
          fontSize: compact ? "10px" : "11px",
          fontWeight: 500,
          color: s.color,
          lineHeight: 1,
          whiteSpace: "nowrap",
          transition: "color 300ms ease",
        }}
      >
        {s.label}
      </Typography>
    </Box>
  );
}

export { StatusBadge };

export function SessionRowSkeleton() {
  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        display: "flex",
        gap: 1.25,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Skeleton
        variant="rounded"
        width={SESSION_ROW_AVATAR_SIZE}
        height={SESSION_ROW_AVATAR_SIZE}
        animation="wave"
        sx={{ borderRadius: SESSION_ROW_AVATAR_RADIUS, flexShrink: 0 }}
      />
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Skeleton variant="text" animation="wave" width="55%" height={14} />
        <Skeleton variant="text" animation="wave" width="90%" height={14} />
        <Skeleton variant="text" animation="wave" width="35%" height={12} />
      </Box>
    </Box>
  );
}

export default function SessionRow({
  session,
  selected,
  onClick,
  showWorkflowProvenanceInChat = true,
  agentName = "Agent",
}) {
  const localUserDisplayName = useProjectStore(
    (state) => state.userDisplayName || state.userName || ""
  );

  const isNew = useRef(true);
  const prevStatusRef = useRef(session.status);
  const [statusAnimating, setStatusAnimating] = useState(false);

  useEffect(() => {
    isNew.current = false;
  }, []);

  useEffect(() => {
    if (prevStatusRef.current !== session.status) {
      prevStatusRef.current = session.status;
      setStatusAnimating(true);
      const timer = setTimeout(() => setStatusAnimating(false), 400);
      return () => clearTimeout(timer);
    }
  }, [session.status]);

  const { state: rowState, senderName } = sessionRowReadiness(
    session,
    localUserDisplayName
  );
  if (rowState === "pending") {
    return <SessionRowSkeleton />;
  }

  const isHydrating = rowState === "hydrating";
  const preview = session.title || "Untitled session";
  const avatarProps = buildNeutralAvatarProps(senderName);
  const replyCount = Math.max(0, (session.messageCount || 0) - 1);
  const replyParticipants =
    replyCount > 0
      ? [senderName, agentName].filter(
          (name, index, arr) => name && arr.indexOf(name) === index
        )
      : [];
  const isWorkflowSession =
    session.source === "WORKFLOW_INVOCATION" ||
    session.source === "WORKFLOW_INVOCATION_ASYNC";

  return (
    <Box
      onClick={onClick}
      sx={{
        px: 2,
        py: 1,
        cursor: "pointer",
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        backgroundColor: selected ? "background.paper" : "transparent",
        borderLeft: "2px solid",
        borderLeftColor: selected ? "text.primary" : "transparent",
        borderBottom: "1px solid",
        borderBottomColor: "divider",
        transition: "background-color 150ms ease, border-color 150ms ease",
        "&:hover": {
          backgroundColor: selected ? "background.paper" : "action.hover",
        },
        ...(isNew.current && {
          animation: `${slideIn} 300ms ease-out both`,
        }),
      }}
    >
      {isHydrating ? (
        <Skeleton
          variant="rounded"
          width={SESSION_ROW_AVATAR_SIZE}
          height={SESSION_ROW_AVATAR_SIZE}
          sx={{
            borderRadius: SESSION_ROW_AVATAR_RADIUS,
            flexShrink: 0,
          }}
        />
      ) : (
        <AgentAvatar
          size={SESSION_ROW_AVATAR_SIZE}
          borderRadius={SESSION_ROW_AVATAR_RADIUS}
          fontSize="11px"
          {...avatarProps}
        />
      )}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: "text.primary",
              lineHeight: 1.35,
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {isHydrating ? (
              <Skeleton variant="text" width="55%" height={14} />
            ) : (
              senderName
            )}
          </Typography>
          <Typography
            sx={{
              fontSize: "10px",
              color: "text.disabled",
              lineHeight: 1.35,
              flexShrink: 0,
            }}
          >
            {formatRelativeTime(session.createdAt)}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: selected ? 500 : 400,
            color: "text.secondary",
            lineHeight: 1.45,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            wordBreak: "break-word",
          }}
        >
          {preview}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.625,
            flexWrap: "wrap",
          }}
        >
          <StatusBadge
            status={session.status}
            animate={statusAnimating}
            compact
          />
          {showWorkflowProvenanceInChat && isWorkflowSession && (
            <Typography sx={{ fontSize: "10px", color: "text.secondary" }}>
              Workflow
            </Typography>
          )}
        </Box>
        {replyCount > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.875,
              minWidth: 0,
              pt: 0.125,
            }}
          >
            <ReplyAvatarStack participants={replyParticipants} />
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                color: "primary.main",
                lineHeight: 1.2,
                flexShrink: 0,
              }}
            >
              {replyCount} {replyCount === 1 ? "reply" : "replies"}
            </Typography>
            {session.updatedAt && (
              <Typography
                sx={{
                  fontSize: "10px",
                  color: "text.secondary",
                  lineHeight: 1.2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                {formatSlackLastReplyTime(session.updatedAt)}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
